import { useEffect, useState } from "react";
import { api, fmtMoney, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Users, Trophy, Wallet, Sparkles, TrendingUp, Pause, CheckCircle2, Archive, Loader2, Crown, Webhook, AlertTriangle, Activity, RefreshCcw } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

function Kpi({ label, value, icon: Icon, tone = "sky", sub, tid }) {
  const toneMap = {
    sky: "from-sky-500/15 to-sky-500/5 border-sky-500/30 text-sky-300",
    emerald: "from-emerald-500/15 to-emerald-500/5 border-emerald-500/30 text-emerald-300",
    amber: "from-amber-500/15 to-amber-500/5 border-amber-500/30 text-amber-300",
    rose: "from-rose-500/15 to-rose-500/5 border-rose-500/30 text-rose-300",
    violet: "from-violet-500/15 to-violet-500/5 border-violet-500/30 text-violet-300",
  };
  return (
    <div data-testid={tid} className={`rounded-xl border bg-gradient-to-br p-4 ${toneMap[tone]}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-widest uppercase font-bold opacity-80">{label}</div>
        <Icon size={16} className="opacity-70"/>
      </div>
      <div className="text-2xl font-black font-mono">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function SuperAdminOverview() {
  const [data, setData] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [scBusy, setScBusy] = useState(false);
  const [scResult, setScResult] = useState(null);
  const [whAttempts, setWhAttempts] = useState(null);
  const [whBusy, setWhBusy] = useState(false);

  useEffect(() => {
    api.get("/superadmin/overview").then((r) => setData(r.data));
    loadWebhookAttempts();
  }, []);

  const loadWebhookAttempts = async () => {
    setWhBusy(true);
    try {
      const r = await api.get("/stripe/webhook/attempts?limit=20");
      setWhAttempts(r.data);
    } catch (e) {
      // non-critical
    } finally { setWhBusy(false); }
  };

  const runHistoricalImport = async () => {
    if (!window.confirm("Popola le 3 stagioni storiche (23/24, 24/25, 25/26) di Lega dei Magnati dai file Excel? L'operazione è idempotente (upsert).")) return;
    setImportBusy(true);
    setImportResult(null);
    try {
      const r = await api.post("/superadmin/import-historical-magnati", {});
      setImportResult(r.data);
      const totalTeams = (r.data.results || []).reduce((s, x) => s + (x.teams || 0), 0);
      toast.success(`✅ Import OK · ${r.data.total_docs} stagioni · ${totalTeams} team totali`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setImportBusy(false); }
  };

  const runSupercoppeInject = async () => {
    if (!window.confirm("Inserisce le 2 Supercoppe mancanti (24/25 As Stronzi · 25/26 Fc Castelverde) in Lega dei Magnati. Idempotente. Procedere?")) return;
    setScBusy(true);
    setScResult(null);
    try {
      const r = await api.post("/superadmin/inject-magnati-supercoppe", {});
      setScResult(r.data);
      toast.success(`Supercoppe inserite (${(r.data.results||[]).length} record)`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setScBusy(false); }
  };

  if (!data) return <div className="p-8 text-slate-400">Caricamento panoramica…</div>;

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <div>
        <div className="text-[11px] tracking-[0.4em] text-rose-400 uppercase font-bold">Console</div>
        <h1 className="text-3xl font-black tracking-tight" data-testid="sa-overview-title">
          Panoramica Piattaforma
        </h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi tid="kpi-total-leagues" label="Leghe totali" value={data.total_leagues} icon={Trophy} tone="amber"/>
        <Kpi tid="kpi-active-leagues" label="Attive" value={data.active_leagues} icon={CheckCircle2} tone="emerald"
             sub={`${data.trial_leagues} in trial · ${data.suspended_leagues} sospese`}/>
        <Kpi tid="kpi-total-users" label="Utenti" value={data.total_users} icon={Users} tone="sky"
             sub={`${data.total_managers} manager · ${data.total_teams} squadre`}/>
        <Kpi tid="kpi-revenue-total" label="Revenue totale" value={`${fmtMoney(data.revenue_total_eur)} €`}
             icon={Wallet} tone="rose"
             sub={`MTD ${fmtMoney(data.revenue_mtd_eur)} € · YTD ${fmtMoney(data.revenue_ytd_eur)} €`}/>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-slate-900/60 border border-slate-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-widest uppercase font-bold text-slate-400">Crescita ultimi 12 mesi</div>
              <div className="text-lg font-bold">Nuove leghe create</div>
            </div>
            <TrendingUp size={18} className="text-emerald-400"/>
          </div>
          <div className="h-64 min-h-[240px]" data-testid="sa-growth-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.growth_12m}>
                <defs>
                  <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.6}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3"/>
                <XAxis dataKey="month" stroke="#64748b" fontSize={11}/>
                <YAxis stroke="#64748b" fontSize={11} allowDecimals={false}/>
                <Tooltip
                  contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 8 }}
                  labelStyle={{ color: "#94a3b8" }}
                  itemStyle={{ color: "#10b981" }}
                />
                <Area type="monotone" dataKey="leagues" stroke="#10b981" fill="url(#growthGrad)" strokeWidth={2}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles size={16} className="text-amber-400"/>
            <div className="text-lg font-bold">Distribuzione Piani</div>
          </div>
          <div className="space-y-3 text-sm">
            <PlanRow label="Attive (pagato)" value={data.active_leagues} total={data.total_leagues} tone="emerald"/>
            <PlanRow label="Trial (non pagate)" value={data.trial_leagues} total={data.total_leagues} tone="amber"/>
            <PlanRow label="Sospese" value={data.suspended_leagues} total={data.total_leagues} tone="rose"/>
            <div className="pt-3 border-t border-slate-800 flex items-center gap-2 text-xs text-slate-500">
              <Pause size={12}/> Founders sono grandfathered (gratis)
            </div>
          </div>
        </div>
      </div>

      {/* Import storico Lega dei Magnati (una tantum) */}
      <div className="bg-gradient-to-br from-slate-900/80 to-slate-950 border border-amber-500/30 rounded-xl p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center shrink-0">
              <Archive className="text-amber-400" size={18}/>
            </div>
            <div>
              <div className="text-lg font-bold text-white">Import Storico Lega dei Magnati</div>
              <div className="text-xs text-slate-400 mt-1 max-w-lg">
                Popola l&apos;archivio (season_history) con le 3 stagioni pregresse (23/24, 24/25, 25/26) — 10 team ognuna, dai file Excel in <code className="text-amber-300">/backend/data/historical/</code>. Idempotente: eseguibile più volte senza duplicare dati.
              </div>
            </div>
          </div>
          <button onClick={runHistoricalImport} disabled={importBusy}
            data-testid="btn-import-historical-magnati"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest bg-gradient-to-br from-amber-400 to-amber-600 text-slate-900 hover:brightness-110 disabled:opacity-30 shadow-lg shadow-amber-500/20 shrink-0">
            {importBusy ? <><Loader2 className="animate-spin" size={13}/> Import in corso…</>
              : <><Archive size={13}/> Esegui import</>}
          </button>
        </div>
        {importResult && (
          <div className="mt-4 pt-4 border-t border-slate-800 grid sm:grid-cols-3 gap-2" data-testid="import-result">
            {(importResult.results || []).map((r, i) => (
              <div key={i} className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs">
                <div className="font-black text-emerald-300">Stagione {r.season}</div>
                <div className="text-slate-300 mt-1">{r.teams} team · {r.modified ? "aggiornata" : "creata"}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Inject Supercoppe storiche Lega dei Magnati (one-shot idempotente) */}
      <div className="bg-gradient-to-br from-slate-900/80 to-slate-950 border border-rose-500/30 rounded-xl p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-500/20 border border-rose-500/40 flex items-center justify-center shrink-0">
              <Crown className="text-rose-400" size={18}/>
            </div>
            <div>
              <div className="text-lg font-bold text-white">Inserisci Supercoppe storiche</div>
              <div className="text-xs text-slate-400 mt-1 max-w-lg">
                Aggiunge nel <code className="text-rose-300">season_history</code> di Lega dei Magnati le 2 Supercoppe pregresse:
                <br/>· Stagione 24/25 → <b>As Stronzi</b> (Andrea Alì)
                <br/>· Stagione 25/26 → <b>Fc Castelverde</b> (Giampaolo Castellani)
                <br/>Idempotente: puoi rieseguirlo senza duplicare.
              </div>
            </div>
          </div>
          <button onClick={runSupercoppeInject} disabled={scBusy}
            data-testid="btn-inject-supercoppe"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest bg-gradient-to-br from-rose-400 to-rose-600 text-slate-900 hover:brightness-110 disabled:opacity-30 shadow-lg shadow-rose-500/20 shrink-0">
            {scBusy ? <><Loader2 className="animate-spin" size={13}/> In corso…</>
              : <><Crown size={13}/> Inserisci Supercoppe</>}
          </button>
        </div>
        {scResult && (
          <div className="mt-4 pt-4 border-t border-slate-800 grid sm:grid-cols-2 gap-2" data-testid="sc-result">
            {(scResult.results || []).map((r, i) => (
              <div key={i} className={`rounded-lg border p-3 text-xs ${r.status === 'ok' ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-amber-500/30 bg-amber-500/10'}`}>
                <div className={`font-black ${r.status === 'ok' ? 'text-emerald-300' : 'text-amber-300'}`}>
                  {r.season_label} · {r.team_name}
                </div>
                <div className="text-slate-300 mt-1">{r.manager} · {r.status === 'ok' ? (r.modified ? 'aggiornato' : (r.matched ? 'già presente' : 'creato')) : r.status}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Monitor Webhook Stripe */}
      <WebhookMonitor data={whAttempts} busy={whBusy} onReload={loadWebhookAttempts}/>
    </div>
  );
}

function WebhookMonitor({ data, busy, onReload }) {
  const stats = data?.stats || {};
  const items = data?.items || [];
  const alerting = (stats.consecutive_fail_head || 0) >= 3;
  const [secretInput, setSecretInput] = useState("");
  const [secretBusy, setSecretBusy] = useState(false);
  const [showSecretPanel, setShowSecretPanel] = useState(false);

  const setSecret = async () => {
    if (!secretInput.startsWith("whsec_")) {
      toast.error("Il secret deve iniziare con whsec_");
      return;
    }
    setSecretBusy(true);
    try {
      await api.post("/stripe/webhook/secret", { secret: secretInput.trim() });
      toast.success("Signing secret salvato — attivo su prossimo webhook");
      setSecretInput("");
      setShowSecretPanel(false);
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSecretBusy(false); }
  };
  const clearSecret = async () => {
    if (!window.confirm("Rimuovere l'override DB e tornare all'env var? Se l'env non è configurato correttamente i webhook falliranno.")) return;
    setSecretBusy(true);
    try {
      await api.delete("/stripe/webhook/secret");
      toast.success("Override rimosso, torna all'env var");
      onReload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSecretBusy(false); }
  };

  const statusStyle = {
    ok: { border: "border-emerald-500/40", bg: "bg-emerald-500/10", text: "text-emerald-300", dot: "bg-emerald-400", label: "OK" },
    signature_error: { border: "border-amber-500/40", bg: "bg-amber-500/10", text: "text-amber-300", dot: "bg-amber-400", label: "SIG" },
    handler_error: { border: "border-rose-500/40", bg: "bg-rose-500/10", text: "text-rose-300", dot: "bg-rose-400", label: "HDL" },
    config_error: { border: "border-rose-500/40", bg: "bg-rose-500/10", text: "text-rose-300", dot: "bg-rose-400", label: "CFG" },
    payload_error: { border: "border-slate-500/40", bg: "bg-slate-500/10", text: "text-slate-300", dot: "bg-slate-400", label: "PAY" },
  };

  return (
    <div className={`bg-gradient-to-br from-slate-900/80 to-slate-950 border rounded-xl p-5 ${alerting ? 'border-rose-500/60 shadow-rose-500/10 shadow-lg' : 'border-sky-500/30'}`} data-testid="webhook-monitor">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 ${alerting ? 'bg-rose-500/20 border-rose-500/40' : 'bg-sky-500/20 border-sky-500/40'}`}>
            {alerting
              ? <AlertTriangle className="text-rose-400" size={18}/>
              : <Webhook className="text-sky-400" size={18}/>}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <div className="text-lg font-bold text-white">Monitor Webhook Stripe</div>
              {alerting && <span className="text-[9px] tracking-widest uppercase font-black text-rose-300 bg-rose-500/20 border border-rose-500/40 px-1.5 py-0.5 rounded" data-testid="webhook-alert-badge">ALERT · {stats.consecutive_fail_head} fallimenti</span>}
            </div>
            <div className="text-xs text-slate-400 mt-1 max-w-lg">
              Ultimi 20 tentativi ricevuti da Stripe sull&apos;endpoint <code className="text-sky-300">/api/stripe/webhook</code>. Alert email automatico se ci sono 3+ fallimenti consecutivi.
            </div>
            {stats.secret_source && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Secret attivo:</span>
                {stats.secret_source === 'db_override' && <span className="text-[10px] font-black text-purple-300 bg-purple-500/15 border border-purple-500/40 px-1.5 py-0.5 rounded" data-testid="secret-source-db">DB override</span>}
                {stats.secret_source === 'env' && <span className="text-[10px] font-black text-emerald-300 bg-emerald-500/15 border border-emerald-500/40 px-1.5 py-0.5 rounded" data-testid="secret-source-env">env</span>}
                {stats.secret_source === 'missing' && <span className="text-[10px] font-black text-rose-300 bg-rose-500/15 border border-rose-500/40 px-1.5 py-0.5 rounded" data-testid="secret-source-missing">NESSUNO</span>}
                {stats.secret_hint && <span className="text-[10px] font-mono text-slate-500">{stats.secret_hint}</span>}
                <button onClick={() => setShowSecretPanel(v => !v)}
                  data-testid="btn-toggle-secret-panel"
                  className="text-[10px] font-black uppercase tracking-widest text-sky-300 hover:text-sky-200 underline underline-offset-2">
                  {showSecretPanel ? 'chiudi' : 'imposta secret'}
                </button>
              </div>
            )}
          </div>
        </div>
        <button onClick={onReload} disabled={busy}
          data-testid="btn-reload-webhook"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black uppercase tracking-widest bg-slate-800 hover:bg-slate-700 border border-slate-700 disabled:opacity-30 text-slate-300 shrink-0">
          {busy ? <Loader2 className="animate-spin" size={13}/> : <RefreshCcw size={13}/>}
          Aggiorna
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
        <StatChip icon={Activity} label="Success rate" value={stats.success_rate != null ? `${stats.success_rate}%` : '—'} tone={stats.success_rate == null ? 'slate' : stats.success_rate >= 95 ? 'emerald' : stats.success_rate >= 80 ? 'amber' : 'rose'} tid="wh-stat-success"/>
        <StatChip icon={CheckCircle2} label="OK (u.100)" value={stats.ok ?? 0} tone="emerald" tid="wh-stat-ok"/>
        <StatChip icon={AlertTriangle} label="Errori (u.100)" value={stats.fail ?? 0} tone={stats.fail > 0 ? 'rose' : 'slate'} tid="wh-stat-fail"/>
        <StatChip icon={Activity} label="Lat. media OK" value={stats.avg_latency_ms_ok != null ? `${stats.avg_latency_ms_ok} ms` : '—'} tone="sky" tid="wh-stat-lat"/>
      </div>

      {/* Secret override panel */}
      {showSecretPanel && (
        <div className="mt-5 rounded-lg border border-purple-500/40 bg-purple-500/5 p-4" data-testid="secret-override-panel">
          <div className="text-sm font-black text-purple-200 mb-1">Imposta signing secret (override DB)</div>
          <div className="text-xs text-slate-400 mb-3 leading-relaxed">
            Bypassa l&apos;env var della piattaforma: il valore viene salvato in MongoDB e usato per validare le firme Stripe. Utile quando la sezione Publish → Secrets non è modificabile. Il valore prende effetto sul <b>prossimo webhook in arrivo</b>, senza redeploy.
          </div>
          <div className="flex gap-2 flex-wrap">
            <input type="password" placeholder="whsec_..."
              value={secretInput} onChange={(e) => setSecretInput(e.target.value)}
              data-testid="input-webhook-secret"
              className="flex-1 min-w-[280px] bg-slate-950 border border-slate-700 focus:border-purple-500/60 rounded-lg px-3 py-2 text-sm font-mono text-white outline-none"/>
            <button onClick={setSecret} disabled={secretBusy || !secretInput.startsWith('whsec_')}
              data-testid="btn-save-secret"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest bg-gradient-to-br from-purple-400 to-purple-600 text-slate-900 hover:brightness-110 disabled:opacity-30 shrink-0">
              {secretBusy ? <Loader2 className="animate-spin" size={13}/> : <CheckCircle2 size={13}/>}
              Salva
            </button>
            {stats.secret_source === 'db_override' && (
              <button onClick={clearSecret} disabled={secretBusy}
                data-testid="btn-clear-secret"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black uppercase tracking-widest bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 shrink-0">
                Rimuovi override
              </button>
            )}
          </div>
        </div>
      )}

      {/* List */}
      <div className="mt-5 space-y-1.5" data-testid="webhook-attempts-list">
        {items.length === 0 ? (
          <div className="text-xs text-slate-500 py-6 text-center">Nessun tentativo webhook registrato. Riceverai eventi qui non appena Stripe ne invia.</div>
        ) : items.map((a) => {
          const s = statusStyle[a.status] || statusStyle.payload_error;
          const dt = a.at ? new Date(a.at) : null;
          const dtStr = dt ? dt.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';
          return (
            <div key={a.attempt_id} className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${s.border} ${s.bg}`}>
              <span className={`w-2 h-2 rounded-full ${s.dot} shrink-0`}/>
              <span className={`text-[10px] font-black tracking-widest ${s.text} w-9 shrink-0`}>{s.label}</span>
              <span className="text-xs text-slate-400 font-mono w-32 shrink-0">{dtStr}</span>
              <span className="text-xs text-slate-300 flex-1 min-w-0 truncate">
                {a.event_type || <span className="text-slate-600 italic">no event type</span>}
                {a.livemode === true && <span className="ml-2 text-[9px] font-black text-emerald-400 bg-emerald-500/10 px-1 rounded">LIVE</span>}
                {a.livemode === false && <span className="ml-2 text-[9px] font-black text-amber-400 bg-amber-500/10 px-1 rounded">TEST</span>}
              </span>
              <span className="text-[10px] text-slate-500 font-mono shrink-0">{a.latency_ms}ms</span>
              <span className="text-[10px] text-slate-500 font-mono shrink-0">HTTP {a.http_code}</span>
              {a.error && (
                <span className="text-[10px] text-rose-400 font-mono truncate max-w-[240px]" title={a.error}>{a.error.slice(0, 40)}{a.error.length > 40 ? '…' : ''}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatChip({ icon: Icon, label, value, tone, tid }) {
  const toneMap = {
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300",
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-300",
    rose: "border-rose-500/30 bg-rose-500/5 text-rose-300",
    sky: "border-sky-500/30 bg-sky-500/5 text-sky-300",
    slate: "border-slate-700 bg-slate-800/40 text-slate-300",
  };
  return (
    <div data-testid={tid} className={`rounded-lg border p-3 ${toneMap[tone] || toneMap.slate}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[9px] tracking-widest uppercase font-bold opacity-80">{label}</div>
        <Icon size={13} className="opacity-60"/>
      </div>
      <div className="text-lg font-black font-mono">{value}</div>
    </div>
  );
}

function PlanRow({ label, value, total, tone }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  const toneBar = { emerald: "bg-emerald-500", amber: "bg-amber-500", rose: "bg-rose-500" }[tone];
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-slate-300">{label}</div>
        <div className="font-mono font-bold text-slate-100">{value} <span className="text-slate-500">· {pct}%</span></div>
      </div>
      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
        <div className={`h-full ${toneBar}`} style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
}
